import { Reducer } from 'react'
import { markActionStatus, treatLocalAction } from '../helpers/context'
import { AUTO_FIELDS } from '../Settings/Sync'
import { CheckPrepareStatusApiResponse, SettingsContextState, CheckInstanceStatusApiResponse,
    STATUS_PREPARE_PENDING, STATUS_ICON_WORKING, INSTANCE_CONTROL_STARTED,
    INSTANCE_CONTROL_STOPPED, STATUS_ICON_FAILED, STATUS_ICON_SUCCESS, INSTANCE_CONTROL_STARTING,
    INSTANCE_CONTROL_STOPPING, STATUS_ICON_UNAVAILABLE, InstanceStatus, InstanceStatusCode,
    GenericSnippet } from '../contexts/SettingsContext.d'
import { AnyAction, ApiResponseAction, PrimaryAction } from '../helpers/action/Action.d'
import { SuperDispatch } from '../helpers/dispatch.d'
import { actionTypeChangedException } from '../helpers/action/action'
import { snippetIdentifier, snippetKey, snippetModifyKey } from '../Settings/SnippetsConfig'
import { CacheModifySnippetAction } from '../helpers/action/SettingsAction.d'

export const SettingsReducer: Reducer<SettingsContextState, AnyAction> = (state, action): SettingsContextState =>
    'apiResponse' in action
        ? SettingsReducerApiResponse(state, action)
        : SettingsReducerLocal(state, action)

const SettingsReducerLocal: Reducer<SettingsContextState, PrimaryAction> = (state, action) => {
    switch (action.type) {
        case 'SYNC_CALL_INITIATED':
            return { ...state,
                sync: {
                    ...state.sync,
                    [action.api]: {
                        [action.request === 'authUrl' ? 'authenticating' : 'loading']: true,
                        text: action.request === 'authUrl'
                            ? 'Please wait...'
                            : 'Synchronising...',
                        authUrl: undefined
                    }
                }
            }
        case 'SET_WINDOW_FOCUSED':
            return {
                ...state,
                windowFocused: action.focused
            }

        case 'RESET_INSTANCE_STATUS':
            delete state.instances?.[action.instanceId]
            return { ...state }

        case 'CACHE_MODIFY_SNIPPET':
            return {
                ...state,
                snippetsPending: {
                    ...state.snippetsPending,
                    modify: {
                        ...state.snippetsPending?.modify,
                        [snippetModifyKey(action.original, action.oldValue)]: action
                    }
                }
            }

        case 'CACHE_CREATE_SNIPPET':
            if (state.snippets?.[snippetIdentifier(action.snippet)][action.snippet.type]?.[snippetKey(action.snippet)]
              && !('aliases' in action.snippet && action.snippet.aliases.length > 0)) {
                // Reject if the snippet already exists
                return state
            }
            return {
                ...state,
                snippetsPending: {
                    ...state.snippetsPending,
                    create: {
                        ...state.snippetsPending?.create,
                        [action.snippet.type]: {
                            ...state.snippetsPending?.create?.[action.snippet.type],
                            [snippetKey(action.snippet)]: action.snippet
                        }
                    }
                }
            }

        default:
            return treatLocalAction(state, action)
    }
}

// This function governs actions which update local state.
export const SettingsReducerApiResponse: Reducer<SettingsContextState, ApiResponseAction> = (state, action) => {
    if (action.type == 'API_REQUEST_FAILED') {
        if (action.sourceAction.type === 'TRIGGER_SYNC') {
            state.sync = state.sync || {}
            try {
                state.sync[action.sourceAction.api] = {
                    loading: false,
                    text: JSON.parse(action.responseText || '')?.error
                }
            } catch (unusedError) {
                state.sync[action.sourceAction.api] = {
                    loading: false,
                    text: action.responseText + ' - try again'
                }
            }
        }
        return treatLocalAction(state, action)
    } else if (action.type === 'API_REQUEST_SUCCEEDED') {
        return treatLocalAction(state, action)
    }

    switch (action.sourceAction.type) {
        case 'TRIGGER_PREPARE':
            state.instances[action.sourceAction.instanceId] = STATUS_PREPARE_PENDING
            action.redispatch({ type: 'CHECK_PREPARE_STATUS', instanceId: 'prepare' })
            return { ...state }

        case 'CHECK_PREPARE_STATUS':
        case 'CHECK_INSTANCE_STATE':
        case 'TURN_INSTANCE_ON':
        case 'TURN_INSTANCE_OFF':
            return updateInstanceState(state, action)

        case 'RETRIEVE_SNIPPETS':
            return markActionStatus({
                ...state,
                snippets: action.apiResponse.data as SettingsContextState['snippets']
            }, action, 'complete')

        case 'GET_SYNC_LOGIN_URL':
            return {
                ...state,
                sync: {
                    ...state.sync,
                    [action.sourceAction.api]: {
                        ...state.sync?.[action.sourceAction.api],
                        authUrl: action.apiResponse?.data as string
                    }
                }
            }

        case 'SYNC_FITBIT_AUTH_CALLBACK':
            setTimeout(() => {
                if (action.sourceAction.type !== 'SYNC_FITBIT_AUTH_CALLBACK')
                    throw actionTypeChangedException(action)
                action.sourceAction.appDispatch({
                    type: 'SAVE_USER_FIELD',
                    field: `${(action.sourceAction).api}_refresh_token`,
                    value: action.apiResponse?.data?.[`${action.sourceAction.api}_refresh_token`] })
                action.sourceAction.appDispatch({
                    type: 'SAVE_USER_FIELD',
                    field: `${action.sourceAction.api}_user_id`,
                    value: action.apiResponse?.data?.[`${action.sourceAction.api}_user_id`] })
            }, 1)
            return {
                ...state,
                sync: {
                    ...state.sync,
                    [action.sourceAction.api]: {}
                }
            }

        case 'UNLINK_SYNC_API':
            setTimeout(() => {
                const { sourceAction } = action
                if (sourceAction.type !== 'UNLINK_SYNC_API')
                    throw actionTypeChangedException(action)
                AUTO_FIELDS.filter(field => field.includes(`${sourceAction.api}_`))
                    .forEach(field => sourceAction.appDispatch(
                        { type: 'SAVE_USER_FIELD', field, value: undefined }
                    ))
            }, 1)
            return state

        case 'TRIGGER_SYNC':
            return { ...state,
                sync: {
                    ...state.sync,
                    [action.sourceAction.api]: {
                        loading: false,
                        text: action.apiResponse?.data as string
                    }
                } }

        case 'CREATE_SNIPPET':
            // fallthrough to MODIFY_SNIPPET
        case 'DELETE_SNIPPET':
            // fallthrough to MODIFY_SNIPPET
        case 'MODIFY_SNIPPET':
            return updateSnippet(state, action)

        default:
            return treatLocalAction(state, action)
    }
}

const updatedSnippet = (action: Omit<CacheModifySnippetAction, 'type'>): GenericSnippet =>
    action.field === 'alias' && 'aliases' in action.original
        ? { ...action.original,
            aliases: action.original.aliases.toSpliced(
                action.original.aliases.indexOf(action.oldValue),
                1,
                action.newValue)
        }
        : {
            ...action.original,
            [action.field]: action.newValue
        }

const updateSnippet: Reducer<SettingsContextState, ApiResponseAction> = (state, action) => {
    if (action.sourceAction.type !== 'DELETE_SNIPPET'
      && action.sourceAction.type !== 'MODIFY_SNIPPET'
      && action.sourceAction.type !== 'CREATE_SNIPPET') {
        return state
    }
    const snippet = action.sourceAction.type === 'MODIFY_SNIPPET'
        ? action.sourceAction.original
        : action.sourceAction.snippet
    const identifier = snippetIdentifier(snippet)
    const key = snippetKey(snippet)

    if (action.sourceAction.type === 'CREATE_SNIPPET') {
        if (!state.snippets) {
            state.snippets = { aliases: {}, attributes: {}, snippets: {} }
        }
        state.snippets = {
            ...state.snippets,
            [identifier]: {
                ...state.snippets?.[identifier],
                [snippet.type]: {
                    ...state.snippets?.[identifier]?.[snippet?.type],
                    [key]: snippet
                }
            }
        }
        delete state.snippetsPending?.create?.[snippet.type][snippetKey(snippet)]
        return state
    }

    if (!state.snippets?.[identifier]?.[snippet.type]?.[key]) {
        return state
    }
    if (action.sourceAction.type === 'MODIFY_SNIPPET') {
        delete state.snippetsPending?.modify?.[snippetModifyKey(action.sourceAction.original, action.sourceAction.oldValue)]
        const updated = updatedSnippet(action.sourceAction)
        if (snippetKey(updated) !== key) {
            updated.snippetKey = key
        }
        state.snippets[identifier][snippet.type][key] = updated
        return { ...state }
    } else {
        if ('canonical' in snippet && action.sourceAction.value !== snippet.canonical) {
            const { aliases } = state.snippets.aliases[snippet.type][key]
            state.snippets.aliases[snippet.type][key].aliases
                .splice(aliases.indexOf(action.sourceAction.value), 1)
            return { ...state }
        } else {
            delete state.snippets[identifier][snippet.type][key]
            return { ...state }
        }
    }
}

const updateInstanceState: Reducer<SettingsContextState, ApiResponseAction> = (state, action) => {
    if (!('instanceId' in action.sourceAction) || !action.apiResponse?.data) {
        return state
    }
    state.instances = state.instances || {}
    const { instanceId } = action.sourceAction
    if (Array.isArray(instanceId)) {
        // Batched check: the response is keyed by instanceId; split it into each instance's status.
        const byInstance = action.apiResponse?.data as Record<string, InstanceStatus | InstanceStatus[]>
        for (const id of instanceId) {
            const value = byInstance[id]
            state.instances[id] = value
                ? instanceStatusFromInfo(Array.isArray(value) ? value : [value])
                : STATUS_ICON_FAILED
        }
        return { ...state }
    }
    if (instanceId === 'prepare') {
        const { data } = (action as CheckPrepareStatusApiResponse).apiResponse
        state.prepare = data
        state.instances[instanceId] = (data?.runningTasks || [])?.length > 0
            ? STATUS_PREPARE_PENDING
            : (data?.latestStoppedTask?.failed || data?.latestCompleteLog?.failed
                ? STATUS_ICON_FAILED
                : STATUS_ICON_SUCCESS)
    } else {
        if (['TURN_INSTANCE_ON', 'TURN_INSTANCE_OFF'].includes(action.type)) {
            state.instances[instanceId]
                = typeof action.apiResponse?.data === 'string' && action.apiResponse.data.includes('No action performed')
                    ? (action.type === 'TURN_INSTANCE_ON' ? INSTANCE_CONTROL_STARTED : INSTANCE_CONTROL_STOPPED)
                    : (action.type === 'TURN_INSTANCE_ON' ? INSTANCE_CONTROL_STARTING : INSTANCE_CONTROL_STOPPING)
        } else {
            const data = (action as CheckInstanceStatusApiResponse).apiResponse.data as InstanceStatus | Record<string, InstanceStatus>
            state.instances[instanceId] = instanceStatusFromInfo(isSingleInstance(data) ? [data] : Object.values(data))
        }
    }
    return { ...state }
}

// A single instance is a bare status object; a multi-resource instance is a map keyed by reference.
const isSingleInstance = (data: InstanceStatus | Record<string, InstanceStatus>): data is InstanceStatus =>
    typeof (data as InstanceStatus).id === 'string'

const instanceStatusFromInfo = (stateInfo: InstanceStatus[]): InstanceStatusCode => {
    if (!stateInfo.every(Boolean)) return STATUS_ICON_WORKING // a partial multi-resource response is indeterminate
    if (stateInfo.every(r => r.unavailable)) return STATUS_ICON_UNAVAILABLE
    if (stateInfo.every(r => r.isRunning)) return INSTANCE_CONTROL_STARTED
    if (stateInfo.every(r => r.isStopped)) return INSTANCE_CONTROL_STOPPED
    if (stateInfo.every(r => r.isStarting)) return INSTANCE_CONTROL_STARTING
    if (stateInfo.every(r => r.isStopping)) return INSTANCE_CONTROL_STOPPING
    return STATUS_ICON_WORKING
}

// This function governs API-calling actions. They may later end up above.
export const SettingsDispatcher: SuperDispatch = (action, dispatch, dispatchToApi) => {
    switch (action.type) {
        case 'CHECK_INSTANCE_STATE':
            return Array.isArray(action.instanceId)
                ? dispatchToApi('admin/instances?ids=' + action.instanceId.join(','), { method: 'GET' })
                : dispatchToApi('admin/instance/' + action.instanceId, { method: 'GET' })

        case 'TURN_INSTANCE_ON':
            return dispatchToApi('admin/instance/' + action.instanceId, { method: 'POST', body: { action: 'start' } })

        case 'TURN_INSTANCE_OFF':
            return dispatchToApi('admin/instance/' + action.instanceId, { method: 'POST', body: { action: 'stop' } })

        case 'CHECK_PREPARE_STATUS':
            return dispatchToApi('user/prepare', { method: 'GET' })

        case 'TRIGGER_PREPARE':
            return dispatchToApi('user/prepare', { method: 'POST' })

        case 'RETRIEVE_SNIPPETS':
            return dispatchToApi('user/snippets', { method: 'GET' })

        case 'MODIFY_SNIPPET':
            return dispatchToApi('user/snippets', { method: 'PUT', body: {
                original: action.original,
                updated: updatedSnippet(action)
            } })

        case 'CREATE_SNIPPET':
            if ('aliases' in action.snippet && action.snippet.aliases.length > 0) {
                return dispatchToApi('user/snippets', { method: 'PUT', body: {
                    original: action.snippet, // API ignores original.aliases
                    updated: action.snippet
                } })
            }
            return dispatchToApi('user/snippets', { method: 'POST', body: { snippet: action.snippet } })

        case 'DELETE_SNIPPET':
            return dispatchToApi('user/snippets', { method: 'DELETE', body: { snippet: action.snippet, deletedValue: action.value } })

        case 'TRIGGER_SYNC':
            dispatch({ type: 'SYNC_CALL_INITIATED', api: action.api })
            return dispatchToApi(`sync/${action.api}`, { method: 'POST' })

        case 'GET_SYNC_LOGIN_URL':
            dispatch({ type: 'SYNC_CALL_INITIATED', api: action.api, request: 'authUrl' })
            return dispatchToApi(`sync/${action.api}/auth`, { method: 'GET' })

        case 'SYNC_FITBIT_AUTH_CALLBACK':
            return dispatchToApi(`sync/${action.api}/auth?code=${action.code}`, { method: 'POST' })

        case 'UNLINK_SYNC_API':
            return dispatchToApi(`sync/${action.api}/auth`, { method: 'DELETE' })

        default:
            return dispatch(action)
    }
}
